import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, Plus, User as UserIcon, AlertTriangle, Clock } from 'lucide-react';
import { listTasks, updateTask, listUsers } from '../../services/api';
import { toast } from '../../hooks/use-toast';
import TaskDetailDialog from './TaskDetailDialog';

const COLUMNS = [
  { key: 'todo',        title: 'To Do',            color: '#94a3b8' },
  { key: 'in_progress', title: 'In Progress',      color: '#0f5c3d' },
  { key: 'review',      title: 'Awaiting Approval', color: '#d97706' },
  { key: 'snoozed',     title: 'Paused',           color: '#8b5cf6' },
  { key: 'done',        title: 'Done',             color: '#16a34a' },
];

const PRI_META = {
  urgent: 'bg-red-600', high: 'bg-orange-500', medium: 'bg-[#0f5c3d]', low: 'bg-gray-400',
};

const KanbanBoard = ({ filter }) => {
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drag, setDrag] = useState(null);
  const [openTaskId, setOpenTaskId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, u] = await Promise.all([
        listTasks(filter || {}),
        listUsers().catch(() => ({ data: [] })),
      ]);
      setTasks(t.data || []);
      setUsers(u.data || []);
    } catch {
      toast({ title: 'Failed to load kanban', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const userLookup = useMemo(() => {
    const m = {};
    users.forEach((u) => { m[u.user_id] = u; });
    return m;
  }, [users]);

  const grouped = useMemo(() => {
    const g = {};
    COLUMNS.forEach((c) => { g[c.key] = []; });
    tasks.forEach((t) => { (g[t.status] || g.todo).push(t); });
    return g;
  }, [tasks]);

  const dropOn = async (colKey) => {
    if (!drag || drag.status === colKey) { setDrag(null); return; }
    const dragging = drag;
    setDrag(null);
    try {
      // Server may rewrite the status (e.g. approval-gated tasks -> 'review' instead of 'done')
      const updated = await updateTask(dragging.id, { status: colKey });
      setTasks((prev) => prev.map((t) => t.id === dragging.id ? { ...t, ...updated } : t));
      const finalLabel = COLUMNS.find((c) => c.key === updated.status)?.title || colKey;
      toast({
        title: updated.status !== colKey ? `Moved to ${finalLabel}` : 'Moved',
        description: updated.status !== colKey
          ? 'Approval required — landed in Awaiting Approval instead of Done.'
          : `to ${finalLabel}`,
      });
    } catch {
      toast({ title: 'Move failed', variant: 'destructive' });
      load();
    }
  };

  return (
    <div data-testid="kanban-board">
      {loading ? (
        <div className="p-12 text-center text-[#0b1a2e]/50">
          <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading tasks…
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
          {COLUMNS.map((col) => (
            <div
              key={col.key}
              data-testid={`kanban-col-${col.key}`}
              onDragOver={(e) => { if (drag) e.preventDefault(); }}
              onDrop={() => dropOn(col.key)}
              className={`bg-[#f7fbf8] border border-black/5 rounded-xl p-3 min-h-[320px] transition ${drag && drag.status !== col.key ? 'ring-2 ring-[#0f5c3d]/30' : ''}`}
            >
              <div className="flex items-center justify-between mb-3 px-1">
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: col.color }} />
                  <span className="text-xs font-semibold uppercase tracking-widest text-[#0b1a2e]">{col.title}</span>
                </div>
                <span className="text-[11px] text-[#0b1a2e]/50 font-mono">{grouped[col.key].length}</span>
              </div>
              <div className="space-y-2">
                {grouped[col.key].map((t) => {
                  const overdue = t.due_date && t.status !== 'done' && new Date(t.due_date) < new Date();
                  return (
                    <div
                      key={t.id}
                      data-testid={`kanban-card-${t.id}`}
                      draggable
                      onDragStart={() => setDrag(t)}
                      onDragEnd={() => setDrag(null)}
                      onClick={() => setOpenTaskId(t.id)}
                      className="bg-white rounded-lg p-3 shadow-sm hover:shadow-md transition cursor-pointer border border-black/5"
                    >
                      <div className="flex items-start gap-2 mb-2">
                        <span className={`w-1.5 h-4 rounded ${PRI_META[t.priority] || PRI_META.medium} flex-shrink-0 mt-0.5`} />
                        <div className="text-sm text-[#0b1a2e] font-medium flex-1 leading-snug">{t.title}</div>
                      </div>
                      <div className="flex items-center gap-2 text-[11px] text-[#0b1a2e]/60">
                        {t.assignee_id && (
                          <span className="inline-flex items-center gap-1" title={userLookup[t.assignee_id]?.name || ''}>
                            <UserIcon className="w-3 h-3" />
                            <span className="truncate max-w-[80px]">{userLookup[t.assignee_id]?.name?.split(' ')[0] || '—'}</span>
                          </span>
                        )}
                        {t.due_date && (
                          <span className={`inline-flex items-center gap-1 ${overdue ? 'text-red-600 font-semibold' : ''}`}>
                            {overdue ? <AlertTriangle className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                            {String(t.due_date).slice(0, 10)}
                          </span>
                        )}
                      </div>
                      {/* Mobile stage selector — HTML5 drag-and-drop doesn't work on touch */}
                      <select
                        value={t.status}
                        onClick={(e) => e.stopPropagation()}
                        onChange={async (e) => {
                          e.stopPropagation();
                          const next = e.target.value;
                          if (next === t.status) return;
                          try {
                            const updated = await updateTask(t.id, { status: next });
                            setTasks((prev) => prev.map((x) => x.id === t.id ? { ...x, ...updated } : x));
                            toast({ title: `Moved to ${COLUMNS.find((c) => c.key === updated.status)?.title || next}` });
                          } catch { toast({ title: 'Move failed', variant: 'destructive' }); load(); }
                        }}
                        data-testid={`kanban-mobile-stage-${t.id}`}
                        className="md:hidden mt-2 w-full text-[11px] px-2 py-1 rounded border border-[#0f5c3d]/20 bg-[#f7fbf8]"
                      >
                        {COLUMNS.map((c) => <option key={c.key} value={c.key}>Move to {c.title}</option>)}
                      </select>
                    </div>
                  );
                })}
                {grouped[col.key].length === 0 && (
                  <div className="text-xs text-[#0b1a2e]/30 italic text-center py-6">Drop tasks here</div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {openTaskId && (
        <TaskDetailDialog
          taskId={openTaskId}
          onClose={() => setOpenTaskId(null)}
          onChanged={load}
        />
      )}
    </div>
  );
};

export default KanbanBoard;
